import cx from 'clsx';
import React, { FunctionComponent } from 'react';
import SwiperCore, { Navigation, Pagination, A11y } from 'swiper';
import { ACFMedia } from 'types/media';
import { Image } from 'components/elements';
import { OnDesktopOnly, OnMobileOnly, OnTabletAndDesktopOnly, OnTabletOnly } from 'utils';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './LogosSlider.module.scss';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import messages from 'messages/base-pl';

SwiperCore.use([Navigation, Pagination, A11y]);

interface OwnProps {
  whiteImages?: boolean;
  logos: ACFMedia[];
  simple?: boolean;
}

type LogoSliderProps = JSX.IntrinsicElements['section'] & OwnProps;

const LogoSlider: FunctionComponent<LogoSliderProps> = props => {
  const { logos, whiteImages = false, simple = false } = props;
  const { formatMessage } = useIntl();

  const logoClassNames = cx({
    [styles.logo]: true,
    [styles.whiteImages]: whiteImages,
  });

  const a11y = {
    prevSlideMessage: formatMessage(messages.prev_slide),
    nextSlideMessage: formatMessage(messages.next_slide),
    paginationBulletMessage: formatMessage(messages.slide_message),
  };

  const renderSlider = (numberOfChunks: number) => {
    const [list, chunkSize] = [[...logos], numberOfChunks];
    const chunks = [...Array(Math.ceil(list.length / chunkSize))].map(_ => list.splice(0, chunkSize));
    const hasChunks = chunks && chunks.length > 1;

    const sliderClassNames = cx({
      [styles.slider]: true,
      [styles.simple]: !hasChunks,
    });

    return (
      <div className={sliderClassNames}>
        <Swiper a11y={a11y} loop={true} navigation pagination={{ clickable: true }}>
          {chunks.map((chunk, index) => (
            <SwiperSlide key={`white-logos-desktop-${index}`}>
              <div className={styles.sliderPage}>
                {chunk
                  .filter(logo => logo)
                  .map((logo, index) => (
                    <div className={logoClassNames} key={`white-logo-desktop-${index}`}>
                      {/* TODO: Responsive check */}
                      <div>
                        <Image width={2} height={1} componentHeight={true} sizes="50vw" acfConfig={logo} />
                      </div>
                    </div>
                  ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  };

  const renderDesktopSlider = (numberOfSlides: number) => {
    const hasSlider = logos.length > numberOfSlides;

    const sliderClassNames = cx({
      [styles.slider]: true,
      [styles.simple]: !hasSlider,
    });

    return (
      <div className={sliderClassNames}>
        <Swiper a11y={a11y} loop={true} slidesPerView={numberOfSlides} navigation pagination={{ clickable: true }}>
          <div className={styles.logos}>
            {logos
              .filter(logo => logo)
              .map((logo, logoIndex) => (
                <SwiperSlide key={`white-logos-desktop-${logoIndex}`}>
                  <div className={styles.logoImage}>
                    {/* TODO: Responsive check */}
                    <Image height={1} width={2} sizes="(max-width: 767px) 50vw, (min-width: 768px) 10vw" acfConfig={logo} />
                  </div>
                </SwiperSlide>
              ))}
          </div>
        </Swiper>
      </div>
    );
  };

  return (
    <div className={styles.logos}>
      <OnMobileOnly>{renderSlider(6)}</OnMobileOnly>
      {!simple ? (
        <Fragment>
          <OnTabletOnly>{renderSlider(12)}</OnTabletOnly>
          <OnDesktopOnly>{renderSlider(18)}</OnDesktopOnly>
        </Fragment>
      ) : (
        <OnTabletAndDesktopOnly>{renderDesktopSlider(5)}</OnTabletAndDesktopOnly>
      )}
    </div>
  );
};

export default LogoSlider;
